import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Stack,
  TextField,
  Typography
} from "@material-ui/core";
import {
  CheckCircleOutline as CompleteIcon,
  ErrorOutline as IncompleteIcon,
  ExpandMore as ExpandMoreIcon,
  ImageNotSupported as NoImageIcon,
  PaymentsOutlined as PriceIcon,
  MeetingRoomOutlined as RoomsIcon,
  SquareFootOutlined as AreaIcon,
  CheckCircle as SavedIcon,
  SaveOutlined as SaveIcon
} from "@material-ui/icons";
import { Image } from "components/Image";
import { formatNumberKeepingDecimals } from "utils/Haim/utils";
import { updateListingAddressFields } from "utils/listings/operation";
import InlineEditableValue from "./InlineEditableValue";
import {
  LISTING_ADDRESS_PART_KEYS,
  addressPartsToListingRecordFields,
  buildAddressPartsFromListing,
  canEditListingRecord,
  getListingAddressRequirements,
  getMissingRequiredAddressParts,
  isNumericAddressPart
} from "./listingAddressRequirements";
import { getAddressPreview, getEffectiveAddressParts } from "./signatureListingContext";
import type {
  ListingAddressPartKey,
  ListingAddressParts,
  ListingAddressRequirements
} from "./listingAddressRequirements";
import type { ListingOverride } from "./signatureListingContext";
import type { TFunction } from "i18next";
import type { ProcessedListingData } from "../../../@types/sharedSchema";

/**
 * "Listing details for this document" — the section that sits under the
 * listing picker in the send form.
 *
 * It exists because a contract has to identify the property it is about, and
 * plenty of listings (especially scraped ones) reach us without a house number
 * or apartment number. Rather than let the realtor discover that after the
 * document is signed, the section:
 *
 *   1. shows, per listing, exactly which address parts are missing;
 *   2. asks for them as structured fields — not one free-text address blob —
 *      so "what's missing" is a question with an answer;
 *   3. previews the resulting address string as the realtor types, including
 *      the before/after-signing split on hidden-addresses sends;
 *   4. keeps complete listings collapsed to a single line, so the section only
 *      demands attention where something is actually wrong.
 *
 * Edits apply to this document by default. When the realtor OWNS the listing
 * (their own Webot listing — not a Yad2/Madlan scrape, not an org-mate's), the
 * card also offers to save the corrected address onto the listing record, so
 * the same gap doesn't have to be filled again on the next document.
 */

export type ListingDetailsSectionProps = {
  listings: ProcessedListingData[];
  overrides: Record<string, ListingOverride>;
  onOverrideChange: (listingId: string, key: keyof ListingOverride, value: string) => void;
  /** Hidden-addresses send: the document shows a redacted address and the full one rides an addendum. */
  hideAddresses: boolean;
  /** Logged-in user's email — decides who may write back to a listing record. */
  currentUserEmail?: string;
  /**
   * Called after a successful write-back so the form can refresh its copy of
   * the listing; without it the card would keep showing the values as
   * document-only overrides on top of a stale record.
   */
  onListingRecordUpdated?: (listingId: string, parts: ListingAddressParts) => void;
};

/** One listing's completeness, computed once and shared by the banner + card. */
type ListingCompleteness = {
  listing: ProcessedListingData;
  listingId: string;
  index: number;
  requirements: ListingAddressRequirements;
  /** The listing's address with the realtor's edits already applied. */
  parts: ListingAddressParts;
  missingParts: ListingAddressPartKey[];
};

export const addressPartLabel = (key: ListingAddressPartKey, t: TFunction): string =>
  t(`signatures.newDoc.listingDetails.field.${key}`) as string;

/**
 * A listing's short human identifier for warning rows elsewhere in the form
 * ("Listing 2 · Herzl St, Tel Aviv"). Reads the POST-edit parts so a row the
 * realtor is halfway through fixing names the address they just typed, not the
 * stale one. Falls back to the ordinal alone when there's nothing to show yet.
 */
export const listingShortLabel = (entry: ListingCompleteness, t: TFunction): string => {
  const ordinal = t("signatures.newDoc.overrideListingLabel", {
    index: entry.index + 1
  }) as string;
  const known = [entry.parts.street, entry.parts.city].filter((p) => p.trim()).join(", ");
  return known ? `${ordinal} · ${known}` : ordinal;
};

/**
 * Every listing's completeness against its property type's rules. Exported so
 * the send form can gate on the same computation the UI displays, rather than
 * re-deriving it and risking the two disagreeing.
 */
export function getListingsCompleteness(
  listings: ProcessedListingData[],
  overrides: Record<string, ListingOverride>
): ListingCompleteness[] {
  return listings
    .filter((listing) => !!listing.id)
    .map((listing, index) => {
      const listingId = listing.id as string;
      const requirements = getListingAddressRequirements(listing.propertyType);
      const parts = getEffectiveAddressParts(listing, overrides[listingId]);
      return {
        listing,
        listingId,
        index,
        requirements,
        parts,
        missingParts: getMissingRequiredAddressParts(parts, requirements)
      };
    });
}

export default function ListingDetailsSection({
  listings,
  overrides,
  onOverrideChange,
  hideAddresses,
  currentUserEmail,
  onListingRecordUpdated
}: ListingDetailsSectionProps): JSX.Element | null {
  const { t } = useTranslation();

  const completeness = useMemo(
    () => getListingsCompleteness(listings, overrides),
    [listings, overrides]
  );

  const incompleteCount = completeness.filter((c) => c.missingParts.length > 0).length;

  // Each card owns whether it's open (see `ListingDetailsCard`), so that typing
  // the last missing field can't yank the card shut underneath the cursor.
  // This counter is the section's only way in: bumping it asks every still-
  // incomplete card to open itself.
  const [expandIncompleteSignal, setExpandIncompleteSignal] = useState(0);

  if (completeness.length === 0) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 0.5 }}
      >
        <Typography variant="subtitle2">
          {t("signatures.newDoc.listingDetails.title")}
        </Typography>
        {incompleteCount > 0 && (
          <Button
            size="small"
            variant="text"
            onClick={() => setExpandIncompleteSignal((n) => n + 1)}
          >
            {t("signatures.newDoc.listingDetails.completeCta")}
          </Button>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25 }}>
        {t("signatures.newDoc.listingDetails.helper")}
      </Typography>

      <Stack spacing={1}>
        {completeness.map((entry) => (
          <ListingDetailsCard
            key={entry.listingId}
            entry={entry}
            override={overrides[entry.listingId]}
            hideAddresses={hideAddresses}
            expandIncompleteSignal={expandIncompleteSignal}
            canSaveToListing={canEditListingRecord(entry.listing, currentUserEmail)}
            onOverrideChange={(key, value) => onOverrideChange(entry.listingId, key, value)}
            onListingRecordUpdated={onListingRecordUpdated}
          />
        ))}
      </Stack>
    </Box>
  );
}

type ListingDetailsCardProps = {
  entry: ListingCompleteness;
  override: ListingOverride | undefined;
  hideAddresses: boolean;
  /** Bumped by the section header's CTA — asks a still-incomplete card to open. */
  expandIncompleteSignal: number;
  /** The realtor owns this Webot listing, so the fix can be persisted to it. */
  canSaveToListing: boolean;
  onOverrideChange: (key: keyof ListingOverride, value: string) => void;
  onListingRecordUpdated?: (listingId: string, parts: ListingAddressParts) => void;
};

function ListingDetailsCard({
  entry,
  override,
  hideAddresses,
  expandIncompleteSignal,
  canSaveToListing,
  onOverrideChange,
  onListingRecordUpdated
}: ListingDetailsCardProps): JSX.Element {
  const { t } = useTranslation();
  const { listing, listingId, index, requirements, parts, missingParts } = entry;

  const isIncomplete = missingParts.length > 0;

  // Open on arrival if this listing is missing something — that's the whole
  // point of the section. Deliberately seeded once at mount rather than
  // derived from `isIncomplete` every render: filling in the last missing
  // field would otherwise collapse the card the realtor is still typing in.
  const [expanded, setExpanded] = useState(isIncomplete);
  const lastHandledSignal = useRef(expandIncompleteSignal);
  useEffect(() => {
    if (expandIncompleteSignal === lastHandledSignal.current) return;
    lastHandledSignal.current = expandIncompleteSignal;
    if (isIncomplete) setExpanded(true);
  }, [expandIncompleteSignal, isIncomplete]);
  const { documentAddress, fullAddress } = getAddressPreview(listing, override, hideAddresses);

  // The header line identifies the listing even while the address is still
  // being filled — an all-blank header would make a multi-listing send
  // impossible to navigate.
  const headerAddress =
    documentAddress ||
    [parts.street, parts.city].filter((p) => p.trim()).join(", ") ||
    (t("signatures.newDoc.overrideListingLabel", { index: index + 1 }) as string);

  const thumbnail = listing.imageUrls?.[0];

  const visibleParts = LISTING_ADDRESS_PART_KEYS.filter(
    (key) => requirements[key] !== "hidden"
  );
  // Two rows: the street line, then whatever narrows it down further. Column
  // spans are computed rather than hard-coded because the second row holds one
  // to three fields depending on the property type.
  const firstRow = visibleParts.filter((key) => key === "street" || key === "houseNumber");
  const secondRow = visibleParts.filter((key) => !firstRow.includes(key));

  // Offer the write-back only once the address in the form differs from the
  // one on the record — with nothing to save, the button is noise.
  const recordParts = buildAddressPartsFromListing(listing);
  const hasUnsavedRecordEdits = LISTING_ADDRESS_PART_KEYS.some(
    (key) => parts[key].trim() !== recordParts[key].trim()
  );

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: isIncomplete ? "warning.main" : "divider",
        bgcolor: isIncomplete ? "rgba(255,193,7,0.06)" : "transparent",
        overflow: "visible"
      }}
    >
      {/* Header — always visible, and the whole row is the expand affordance
          so there's no small target to hunt for on a phone. */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        onClick={() => setExpanded((v) => !v)}
        sx={{ p: 1.25, cursor: "pointer", userSelect: "none" }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: 1,
            overflow: "hidden",
            bgcolor: "background.neutral",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          {thumbnail ? (
            <Image
              src={thumbnail}
              alt={headerAddress}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <NoImageIcon sx={{ color: "text.disabled", fontSize: 20 }} />
          )}
        </Box>

        {/* Address on top, then property type + status. The listing's ordinal
            ("נכס 1") used to caption every card, but with the address right
            there it only added a number to read past — it survives solely as
            the fallback for a listing with no address text yet. */}
        <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.5}>
          <Typography variant="subtitle2" noWrap>
            {headerAddress}
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ flexWrap: "wrap", gap: 0.5, minWidth: 0 }}
          >
            {listing.propertyType && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {listing.propertyType}
              </Typography>
            )}
            <Chip
              size="small"
              variant={isIncomplete ? "filled" : "outlined"}
              color={isIncomplete ? "warning" : "success"}
              icon={
                isIncomplete ? (
                  <IncompleteIcon fontSize="small" />
                ) : (
                  <CompleteIcon fontSize="small" />
                )
              }
              label={
                // Explicit singular/plural keys rather than i18next's `count`
                // pluralisation: we're on i18next v20, whose JSON-v3 resolver wants
                // `_plural` (EN) / numeric suffixes (HE) — the v4-style `_one`/
                // `_other` keys silently fail and render the raw key instead.
                isIncomplete
                  ? missingParts.length === 1
                    ? (t("signatures.newDoc.listingDetails.missingChipOne") as string)
                    : (t("signatures.newDoc.listingDetails.missingChipMany", {
                        count: missingParts.length
                      }) as string)
                  : (t("signatures.newDoc.listingDetails.completeChip") as string)
              }
              sx={{ flexShrink: 0 }}
            />
          </Stack>
        </Stack>

        <ExpandMoreIcon
          fontSize="small"
          sx={{
            flexShrink: 0,
            color: "text.secondary",
            transform: expanded ? "rotate(180deg)" : "none",
            transition: "transform 150ms ease"
          }}
        />
      </Stack>

      <Collapse in={expanded} unmountOnExit>
        <Divider />
        <Stack spacing={1.5} sx={{ p: 1.5 }}>
          <Box
            sx={{
              display: "grid",
              gap: 1.25,
              gridTemplateColumns: { xs: "1fr", sm: "repeat(6, 1fr)" }
            }}
          >
            {firstRow.map((key) => (
              <AddressPartInput
                key={key}
                partKey={key}
                value={parts[key]}
                requirement={requirements[key]}
                isMissing={missingParts.includes(key)}
                span={key === "street" ? 4 : 2}
                onChange={(value) => onOverrideChange(key, value)}
              />
            ))}
            {secondRow.map((key) => (
              <AddressPartInput
                key={key}
                partKey={key}
                value={parts[key]}
                requirement={requirements[key]}
                isMissing={missingParts.includes(key)}
                span={Math.floor(6 / Math.max(secondRow.length, 1))}
                onChange={(value) => onOverrideChange(key, value)}
              />
            ))}
          </Box>

          {/* The address the document will carry, echoed back live. On a
              hidden send the preview shows the address disclosed AFTER signing,
              and the redacted one the signer sees first sits below it — both as
              plain text, each one click away from being editable. */}
          <AddressPreview
            label={
              hideAddresses
                ? (t("signatures.newDoc.listingDetails.previewAfterSigning") as string)
                : (t("signatures.newDoc.listingDetails.previewTitle") as string)
            }
            value={hideAddresses ? fullAddress : documentAddress}
          />

          {hideAddresses && (
            <Box sx={{ borderRadius: 1, p: 0.5, bgcolor: "rgba(255,193,7,0.08)" }}>
              <InlineEditableValue
                variant="block"
                label={t("signatures.newDoc.listingDetails.redactedPreviewLabel") as string}
                value={documentAddress}
                onChange={(next) => onOverrideChange("redactedAddress", next)}
              />
            </Box>
          )}

          <ListingFiguresRow
            listing={listing}
            override={override}
            onOverrideChange={onOverrideChange}
          />

          {canSaveToListing && (
            <SaveToListingAction
              listing={listing}
              listingId={listingId}
              parts={parts}
              hasUnsavedRecordEdits={hasUnsavedRecordEdits}
              onSaved={onListingRecordUpdated}
            />
          )}
        </Stack>
      </Collapse>
    </Card>
  );
}

type AddressPartInputProps = {
  partKey: ListingAddressPartKey;
  value: string;
  requirement: ListingAddressRequirements[ListingAddressPartKey];
  isMissing: boolean;
  /** Columns to occupy in the 6-column desktop grid. */
  span: number;
  onChange: (value: string) => void;
};

function AddressPartInput({
  partKey,
  value,
  requirement,
  isMissing,
  span,
  onChange
}: AddressPartInputProps): JSX.Element {
  const { t } = useTranslation();
  const numeric = isNumericAddressPart(partKey);

  const helperText = isMissing
    ? (t("signatures.newDoc.listingDetails.requiredHelper") as string)
    : partKey === "floor"
    ? (t("signatures.newDoc.listingDetails.floorHelper") as string)
    : requirement === "optional"
    ? (t("signatures.newDoc.listingDetails.optionalHelper") as string)
    : " ";

  return (
    <Box sx={{ gridColumn: { xs: "auto", sm: `span ${span}` } }}>
      <TextField
        size="small"
        fullWidth
        // `type="number"` would let the browser's spinner + locale parsing
        // mangle a house number like "12א"; `inputMode` gets the numeric
        // keypad on mobile without giving up the raw string.
        inputProps={numeric ? { inputMode: "numeric", dir: "ltr" } : undefined}
        label={addressPartLabel(partKey, t)}
        required={requirement === "required"}
        error={isMissing}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        helperText={helperText}
        InputLabelProps={{ shrink: true }}
      />
    </Box>
  );
}

/**
 * The address string this listing will actually contribute, echoed back live.
 * The realtor edits parts; this is the sentence those parts add up to — the
 * one thing they'd otherwise only see after sending.
 */
function AddressPreview({ label, value }: { label: string; value: string }): JSX.Element {
  const { t } = useTranslation();

  return (
    // Caption above the address, never beside it: these labels are full
    // sentences ("כתובת מלאה שתיחשף לאחר החתימה:") and sharing a line with a
    // long address left neither of them readable.
    <Stack spacing={0.25} sx={{ p: 1.25, borderRadius: 1, bgcolor: "background.neutral" }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, color: value ? "text.primary" : "text.disabled" }}
      >
        {value || (t("signatures.newDoc.listingDetails.previewEmpty") as string)}
      </Typography>
    </Stack>
  );
}

/**
 * Price / rooms / square meters, as a glanceable icon row.
 *
 * These are almost always already on the listing and none of them gate a send,
 * so rendering three permanent text inputs made the card look like a form to
 * fill rather than a summary to check — and buried the address fields that
 * genuinely need attention. Read-only by default, one click to edit, "—" when
 * a value is absent so a gap is still visible.
 */
function ListingFiguresRow({
  listing,
  override,
  onOverrideChange
}: {
  listing: ProcessedListingData;
  override: ListingOverride | undefined;
  onOverrideChange: (key: keyof ListingOverride, value: string) => void;
}): JSX.Element {
  const { t } = useTranslation();

  const figures = [
    {
      key: "price" as const,
      label: t("signatures.listingsBlock.column.price") as string,
      icon: <PriceIcon sx={{ fontSize: 17, color: "text.secondary", flexShrink: 0 }} />,
      base: (listing as any).price ?? (listing as any).askingPrice,
      suffix: "₪"
    },
    {
      key: "rooms" as const,
      label: t("signatures.listingsBlock.column.rooms") as string,
      icon: <RoomsIcon sx={{ fontSize: 17, color: "text.secondary", flexShrink: 0 }} />,
      base: (listing as any).roomsCount ?? (listing as any).rooms,
      suffix: ""
    },
    {
      key: "squareMeter" as const,
      label: t("signatures.listingsBlock.column.square_meter") as string,
      icon: <AreaIcon sx={{ fontSize: 17, color: "text.secondary", flexShrink: 0 }} />,
      base: (listing as any).squareMeter,
      // Reuses the column label as the unit rather than inventing a key —
      // "95" next to "4" is otherwise ambiguous at a glance.
      suffix: ` ${t("signatures.listingsBlock.column.square_meter")}`
    }
  ];

  const rawValue = (key: "price" | "rooms" | "squareMeter", base: unknown): string =>
    override?.[key] !== undefined
      ? (override[key] as string)
      : base === undefined || base === null
      ? ""
      : String(base);

  // Room counts are whole or half, nothing else (same rule as the listing edit
  // dialog's rooms input). "4." is admitted as the unavoidable step on the way
  // to "4.5"; a keystroke that would produce anything outside the pattern is
  // simply not applied.
  const ROOMS_INPUT_PATTERN = /^\d{0,2}(\.5?)?$/;

  return (
    // Fixed slots, not a wrapping flex row: each figure keeps the same width
    // whether it's showing text or an input, so clicking one doesn't resize it
    // or shuffle the other two.
    <Box
      sx={{
        display: "grid",
        gap: 1,
        alignItems: "center",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }
      }}
    >
      {figures.map((figure) => {
        const raw = rawValue(figure.key, figure.base);
        const asNumber = Number(raw);
        // Thousands separators for readability, but only when the value really
        // is numeric — a half-typed "3." must survive a re-render intact. The
        // decimals-keeping formatter matters: rounding turned "4.5" rooms into
        // a displayed "5", which read as the half room being rejected even
        // though the document itself carried 4.5.
        const display = raw
          ? `${Number.isFinite(asNumber) && raw.trim() !== "" ? formatNumberKeepingDecimals(asNumber) : raw}${figure.suffix}`
          : "";
        return (
          <InlineEditableValue
            key={figure.key}
            numeric
            label={figure.label}
            value={raw}
            displayValue={display}
            icon={figure.icon}
            onChange={(next) => {
              if (figure.key === "rooms" && !ROOMS_INPUT_PATTERN.test(next.trim())) return;
              onOverrideChange(figure.key, next);
            }}
          />
        );
      })}
    </Box>
  );
}

/**
 * "Also update the listing record" — the escape from filling the same gap on
 * every document.
 *
 * Only rendered for a Webot listing the realtor owns (see `canEditListingRecord`),
 * and only once the form's address actually differs from the stored one. The
 * write is explicit rather than riding along with the send: it mutates data
 * outside this document, so it deserves its own click and its own confirmation.
 */
function SaveToListingAction({
  listing,
  listingId,
  parts,
  hasUnsavedRecordEdits,
  onSaved
}: {
  listing: ProcessedListingData;
  listingId: string;
  parts: ListingAddressParts;
  hasUnsavedRecordEdits: boolean;
  onSaved?: (listingId: string, parts: ListingAddressParts) => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const [state, setState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  // A fresh edit after a save re-arms the button — otherwise the second
  // correction in a row would have no way to be persisted.
  useEffect(() => {
    if (hasUnsavedRecordEdits) setState("idle");
  }, [hasUnsavedRecordEdits]);

  if (!hasUnsavedRecordEdits && state !== "saved") return null;

  const save = async () => {
    setState("saving");
    try {
      // `originalListingId` is the `real-estate-listings` document; `listing.id`
      // is the processed copy in the search index, which is not writable.
      await updateListingAddressFields(
        listing.originalListingId as string,
        addressPartsToListingRecordFields(parts)
      );
      setState("saved");
      onSaved?.(listingId, parts);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to save listing address", err);
      setState("error");
    }
  };

  if (state === "saved") {
    return (
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: "success.main" }}>
        <SavedIcon sx={{ fontSize: 17 }} />
        <Typography variant="caption">
          {t("signatures.newDoc.listingDetails.saveToListingSaved")}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={0.25} alignItems="flex-start">
      <Button
        size="small"
        variant="outlined"
        disabled={state === "saving"}
        onClick={save}
        startIcon={
          state === "saving" ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />
        }
        sx={{ textTransform: "none" }}
      >
        {t(
          state === "saving"
            ? "signatures.newDoc.listingDetails.saveToListingSaving"
            : "signatures.newDoc.listingDetails.saveToListing"
        )}
      </Button>
      <Typography
        variant="caption"
        color={state === "error" ? "error.main" : "text.secondary"}
        sx={{ px: 0.5 }}
      >
        {t(
          state === "error"
            ? "signatures.newDoc.listingDetails.saveToListingError"
            : "signatures.newDoc.listingDetails.saveToListingHelper"
        )}
      </Typography>
    </Stack>
  );
}
